import { type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCurrentIdentity, type Permission } from '@/lib/identity-context';

interface PermissionGateProps {
  permission: Permission;
  children: ReactNode;
}

// UX gate for admin routes. Renders a 404-style "page not found" block when
// the caller's membership doesn't carry `permission`. Server enforcement is
// authoritative — every admin handler re-checks via `authorize()` middleware
// at the Worker — so this exists only to keep an unprivileged caller from
// staring at an empty page.
export function PermissionGate({ permission, children }: PermissionGateProps) {
  const { hasPermission } = useCurrentIdentity();
  if (!hasPermission(permission)) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="permission-gate-404">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Page not found</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            The page you’re looking for doesn’t exist.
          </CardContent>
        </Card>
      </div>
    );
  }
  return <>{children}</>;
}
